/**
 * The creator's country and time zone, from the profile of the platform they are outreached on (owner ruling
 * 2026-10-11; Go #739). The zone is always a real IANA zone or unknown, never the campaign zone; it is only
 * compared with the campaign zone.
 */
import { useId, useState } from 'react';
import { type QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { creators, type CreatorGeo, type CreatorGeoPlatform, type PlatformGeo, type UpdateCreatorGeoBody } from '@discovr/api';
import { useCan } from '@discovr/common';
import { SCOPES } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { Label } from '../../../components/ui/label';
import { TimeZoneSelect } from '../../../components/common/TimeZoneSelect';
import { countryName, countryOptions } from '../../../lib/countries';
import { canonicalId, zoneLabel } from '../../../lib/timeZones';
import { statusOf } from './sendLabels';

const PLATFORM_NAMES: Record<CreatorGeoPlatform, string> = { instagram: 'Instagram', youtube: 'YouTube' };

const COUNTRY_SOURCES: Record<string, string> = { phyllo: 'Phyllo', youtube_api: 'YouTube' };
const TIMEZONE_SOURCES: Record<string, string> = { derived: 'Set from the country', phyllo: 'Set from the state on Phyllo' };
const ADMIN_SOURCE = 'Set by an admin';

const SAVE_ERRORS: Record<number, (platform: string) => string> = {
  400: () => "That country or time zone isn't accepted. Pick another.",
  403: () => "You don't have permission to change a creator's country or time zone.",
  404: (platform) => `This creator has no ${platform} profile any more. Refresh the page.`,
};
const CONFLICT_NOTICE = "This creator's country or time zone changed while you were editing. The latest values are shown; edit again if you still need to.";
/** The picker always offers bare "UTC"; Go accepts only Area/Location names, so it is sent as Etc/UTC. */
const GO_ZONE_NAMES: Record<string, string> = { UTC: 'Etc/UTC' };
const GENERIC_SAVE_ERROR = "Couldn't save the country and time zone. Try again.";

const creatorGeoQueryKey = (userId: string) => ['creator-geo', userId] as const;

/** Outreach rows and drafts carry the same profile geo (profile_geo), in every campaign the creator is in. */
function invalidateOutreachRows(queryClient: QueryClient) {
  void queryClient.invalidateQueries({
    // ['campaign', id, 'outreach', 'rows', …] and ['campaign', 'detail', id, 'outreach', 'drafts', …].
    predicate: ({ queryKey: k }) => k[0] === 'campaign' && k.some((part, i) => part === 'outreach' && (k[i + 1] === 'rows' || k[i + 1] === 'drafts')),
  });
}

function storeGeo(queryClient: QueryClient, userId: string, platform: CreatorGeoPlatform, stored: PlatformGeo) {
  queryClient.setQueryData<CreatorGeo>(creatorGeoQueryKey(userId), (prev) => (prev ? { ...prev, [platform]: stored } : prev));
  invalidateOutreachRows(queryClient);
}

function saveErrorCopy(status: number | undefined, platformName: string): string {
  const copy = status === undefined ? undefined : SAVE_ERRORS[status];
  return copy ? copy(platformName) : GENERIC_SAVE_ERROR;
}

function geoPlatform(platform: string): CreatorGeoPlatform | null {
  return platform === 'instagram' || platform === 'youtube' ? platform : null;
}

function countrySourceLine(geo: PlatformGeo, platformName: string): string | null {
  if (!geo.country || !geo.country_source) return null;
  if (geo.country_source === 'admin') return ADMIN_SOURCE;
  const from = COUNTRY_SOURCES[geo.country_source];
  return `From the ${platformName} profile${from ? ` (${from})` : ''}`;
}

function timezoneSourceLine(geo: PlatformGeo): string | null {
  if (!geo.timezone || !geo.timezone_source) return null;
  return geo.timezone_source === 'admin' ? ADMIN_SOURCE : (TIMEZONE_SOURCES[geo.timezone_source] ?? null);
}

function zoneMarker(timezone: string | undefined, campaignZone: string | undefined): { matches: boolean; text: string } | null {
  if (!timezone || !campaignZone) return null;
  if (canonicalId(timezone) === canonicalId(campaignZone)) return { matches: true, text: 'Matches the campaign time zone' };
  return { matches: false, text: `Doesn't match the campaign time zone (${zoneLabel(canonicalId(campaignZone))})` };
}

export interface CreatorGeoSectionProps {
  /** The creator's user id (ObjectID hex). */
  userId: string;
  /** The campaign's platform: the profile sending reads. */
  platform: string;
  campaignZone?: string;
}

/** Keyed by creator and platform: nothing being edited for one creator survives a switch to another. */
export function CreatorGeoSection(props: CreatorGeoSectionProps) {
  return <GeoSectionBody key={`${props.userId}:${props.platform}`} {...props} />;
}

function GeoSectionBody({ userId, platform, campaignZone }: CreatorGeoSectionProps) {
  const key = geoPlatform(platform);
  const headingId = useId();
  const query = useQuery({ queryKey: creatorGeoQueryKey(userId), queryFn: () => creators.getGeo(userId), enabled: !!userId && key !== null });
  const canEdit = useCan(SCOPES.creatorUpdate);
  const [editing, setEditing] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);
  if (!key) return null;
  const platformName = PLATFORM_NAMES[key];
  const geo = query.data?.[key];

  return (
    <section aria-labelledby={headingId} className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 id={headingId} className="text-body-sm-medium text-foreground">
          Country and time zone
        </h3>
        {canEdit && geo && !editing && (
          <button
            type="button"
            aria-label="Edit country and time zone"
            className="text-body-sm-regular text-primary hover:underline"
            onClick={() => {
              setNotice(null);
              setEditing(true);
            }}
          >
            Edit
          </button>
        )}
      </div>
      {query.isPending ? (
        <p className="text-body-sm-regular text-muted-foreground">Loading…</p>
      ) : query.isError && !query.data ? (
        <div className="flex items-center justify-between gap-2">
          <p className="text-body-sm-regular text-destructive">Couldn&apos;t load the country and time zone.</p>
          <Button type="button" variant="outline" size="sm" onClick={() => void query.refetch()}>
            Try again
          </Button>
        </div>
      ) : !geo ? (
        <p className="text-body-sm-regular text-muted-foreground">This creator has no {platformName} profile, so there&apos;s no country or time zone.</p>
      ) : editing ? (
        <GeoEditForm
          userId={userId}
          platform={key}
          platformName={platformName}
          geo={geo}
          onDone={() => setEditing(false)}
          onConflict={() => {
            setNotice(CONFLICT_NOTICE);
            setEditing(false);
          }}
        />
      ) : (
        <>
          {notice && (
            <p role="alert" className="text-body-sm-regular text-amber-700">
              {notice}
            </p>
          )}
          <GeoView geo={geo} platformName={platformName} campaignZone={campaignZone} />
          {canEdit && (
            <BackToAutomatic
              userId={userId}
              platform={key}
              platformName={platformName}
              geo={geo}
              onConflict={() => setNotice(CONFLICT_NOTICE)}
              onStart={() => setNotice(null)}
            />
          )}
        </>
      )}
    </section>
  );
}

function GeoView({ geo, platformName, campaignZone }: { geo: PlatformGeo; platformName: string; campaignZone?: string }) {
  const countrySource = countrySourceLine(geo, platformName);
  const zoneSource = timezoneSourceLine(geo);
  const marker = zoneMarker(geo.timezone, campaignZone);
  return (
    <dl className="space-y-2 text-body-sm-regular">
      <div>
        <dt className="text-muted-foreground">Country</dt>
        <dd className="text-foreground">{geo.country ? countryName(geo.country) : 'Not set'}</dd>
        {countrySource && <dd className="text-[12px] text-muted-foreground">{countrySource}</dd>}
      </div>
      <div>
        <dt className="text-muted-foreground">Time zone</dt>
        <dd className="text-foreground">{geo.timezone ? zoneLabel(canonicalId(geo.timezone)) : 'Not set'}</dd>
        {zoneSource && <dd className="text-[12px] text-muted-foreground">{zoneSource}</dd>}
        {marker && (
          <dd>
            <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${marker.matches ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
              {marker.text}
            </span>
          </dd>
        )}
      </div>
    </dl>
  );
}

type GeoField = 'country' | 'timezone';

interface BackToAutomaticProps {
  userId: string;
  platform: CreatorGeoPlatform;
  platformName: string;
  geo: PlatformGeo;
  onConflict: () => void;
  onStart: () => void;
}

/**
 * Clears an admin-set country or zone so the automatic source applies again (Go: "" clears; a cleared Instagram
 * country comes back from Phyllo at once, a cleared YouTube one on the next profile refresh, a cleared zone is
 * re-derived from the country).
 */
function BackToAutomatic({ userId, platform, platformName, geo, onConflict, onStart }: BackToAutomaticProps) {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const fields: GeoField[] = [];
  if (geo.country_source === 'admin') fields.push('country');
  if (geo.timezone_source === 'admin') fields.push('timezone');
  const clear = useMutation({
    mutationFn: (field: GeoField) => creators.updateGeo(userId, platform, { [field]: '' }),
    onSuccess: ({ platform: _platform, ...stored }) => storeGeo(queryClient, userId, platform, stored),
    onError: (err: unknown) => {
      const status = statusOf(err);
      if (status === 409 || status === 404) void queryClient.invalidateQueries({ queryKey: creatorGeoQueryKey(userId) });
      if (status === 409) {
        onConflict();
        return;
      }
      setError(saveErrorCopy(status, platformName));
    },
  });
  if (fields.length === 0) return null;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap gap-2">
        {fields.map((field) => (
          <Button
            key={field}
            type="button"
            variant="outline"
            size="sm"
            disabled={clear.isPending}
            loading={clear.isPending && clear.variables === field}
            onClick={() => {
              setError(null);
              onStart();
              clear.mutate(field);
            }}
          >
            {field === 'country' ? 'Country back to automatic' : 'Time zone back to automatic'}
          </Button>
        ))}
      </div>
      {error && (
        <p role="alert" className="text-body-sm-regular text-destructive">
          {error}
        </p>
      )}
    </div>
  );
}

interface GeoEditFormProps {
  userId: string;
  platform: CreatorGeoPlatform;
  platformName: string;
  geo: PlatformGeo;
  onDone: () => void;
  onConflict: () => void;
}

function GeoEditForm({ userId, platform, platformName, geo, onDone, onConflict }: GeoEditFormProps) {
  const queryClient = useQueryClient();
  const countryId = useId();
  const baseCountry = geo.country ?? '';
  const baseZone = geo.timezone ? canonicalId(geo.timezone) : '';
  const [country, setCountry] = useState(baseCountry);
  const [timezone, setTimezone] = useState(baseZone);
  const [error, setError] = useState<string | null>(null);

  const body: UpdateCreatorGeoBody = {
    ...(country !== baseCountry ? { country } : {}),
    ...(timezone !== baseZone ? { timezone: GO_ZONE_NAMES[timezone] ?? timezone } : {}),
  };
  const save = useMutation({
    mutationFn: () => creators.updateGeo(userId, platform, body),
    onSuccess: ({ platform: _platform, ...stored }) => {
      storeGeo(queryClient, userId, platform, stored);
      onDone();
    },
    onError: (err: unknown) => {
      const status = statusOf(err);
      if (status === 409 || status === 404) void queryClient.invalidateQueries({ queryKey: creatorGeoQueryKey(userId) });
      if (status === 409) {
        onConflict();
        return;
      }
      setError(saveErrorCopy(status, platformName));
    },
  });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={countryId}>Country</Label>
        <select
          id={countryId}
          value={country}
          onChange={(e) => setCountry(e.target.value)}
          className="h-9 rounded-lg border border-border bg-transparent px-3 text-body-sm-regular text-foreground"
        >
          {country === '' && (
            <option value="" disabled>
              Not set
            </option>
          )}
          {countryOptions().map((c) => (
            <option key={c.code} value={c.code}>
              {c.name}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-col gap-1.5">
        <span aria-hidden="true" className="text-body-sm-medium text-foreground">
          Time zone
        </span>
        <TimeZoneSelect label="Time zone" value={timezone || null} onChange={(id) => id && setTimezone(id)} />
        <p className="text-caption-regular text-muted-foreground">
          Applies to this creator&apos;s {platformName} profile everywhere. A new country with a single time zone sets it,
          unless an admin already set the zone.
        </p>
      </div>
      {error && (
        <p role="alert" className="text-body-sm-regular text-destructive">
          {error}
        </p>
      )}
      <div className="flex items-center justify-end gap-2">
        <Button type="button" variant="outline" onClick={onDone}>
          Cancel
        </Button>
        <Button type="button" disabled={Object.keys(body).length === 0 || save.isPending} loading={save.isPending} onClick={() => { setError(null); save.mutate(); }}>
          Save
        </Button>
      </div>
    </div>
  );
}
